import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface InlineEditTextProps {
	/** The text currently shown. */
	value: string;
	/** Shown (muted) when `value` is empty and the text is editable. */
	placeholder?: string;
	/** Double-click swaps the text for an input; off → plain text. */
	editable: boolean;
	/** Called with the trimmed text on Enter or blur. Escape discards. */
	onSave: (value: string) => void;
	/** Wrapper element for the read view. */
	as?: "h3" | "p";
	/** Applied to both the read view and the input so the swap keeps the typography. */
	className?: string;
	/** Tooltip in read view when not editable. */
	title?: string;
	/** Tooltip in read view when editable. */
	editHint?: string;
}

/**
 * Text that turns into an inline input on double-click. A plain input is used
 * instead of contentEditable so Enter/Escape/paste behave predictably and the
 * value stays a controlled string.
 */
const InlineEditText: React.FC<InlineEditTextProps> = ({
	value,
	placeholder,
	editable,
	onSave,
	as: Tag = "p",
	className,
	title,
	editHint,
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value);
	const inputRef = useRef<HTMLInputElement>(null);
	// Escape must not also trigger the blur-save.
	const cancelled = useRef(false);

	useEffect(() => {
		if (!editable) setEditing(false);
	}, [editable]);

	useEffect(() => {
		if (editing) inputRef.current?.select();
	}, [editing]);

	const start = () => {
		if (!editable) return;
		cancelled.current = false;
		setDraft(value);
		setEditing(true);
	};

	const commit = () => {
		setEditing(false);
		if (cancelled.current) return;
		const next = draft.trim();
		if (next !== value) onSave(next);
	};

	if (editing) {
		return (
			<input
				ref={inputRef}
				autoFocus
				value={draft}
				placeholder={placeholder}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						inputRef.current?.blur();
					} else if (e.key === "Escape") {
						e.preventDefault();
						cancelled.current = true;
						inputRef.current?.blur();
					}
					// Keep grid/dialog key handlers out of the input.
					e.stopPropagation();
				}}
				onMouseDown={(e) => e.stopPropagation()}
				className={cn(
					className,
					"-ml-1 w-full min-w-0 flex-1 rounded bg-white px-1 outline-none ring-1 ring-action placeholder:font-normal placeholder:text-gray-400"
				)}
			/>
		);
	}

	if (!value && !(editable && placeholder)) return null;

	return (
		<Tag
			className={cn(
				className,
				editable && "cursor-text",
				!value && "font-normal italic text-gray-400"
			)}
			title={editable ? editHint : title}
			onDoubleClick={editable ? start : undefined}
		>
			{value || placeholder}
		</Tag>
	);
};

export default InlineEditText;
